import type { MessageAttachment } from '@/types'
import { getSupabase } from '@/hooks/useAuth'

import { API_BASE } from '@/lib/config'

export interface PendingMessageAttachment {
  file: File
  name: string
  type: string
  url: string
  size: number
}

const INLINE_ATTACHMENT_EXTENSIONS = new Set([
  'txt', 'md', 'mdx', 'json', 'js', 'jsx', 'ts', 'tsx', 'py', 'html', 'css',
  'scss', 'xml', 'yaml', 'yml', 'csv', 'sql', 'sh', 'bash', 'env', 'log',
])

const UPLOADABLE_ATTACHMENT_EXTENSIONS = new Set([
  'txt', 'md', 'mdx', 'json', 'js', 'jsx', 'ts', 'tsx', 'py', 'html', 'css',
  'scss', 'xml', 'yaml', 'yml', 'csv', 'sql', 'sh', 'bash', 'env', 'log',
  'pdf', 'doc', 'docx', 'xlsx', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'heic', 'heif',
])

const MAX_INLINE_ATTACHMENT_SIZE = 180_000
const MAX_INLINE_ATTACHMENT_CHARS = 24_000

function getAttachmentExtension(filename: string): string {
  const parts = filename.toLowerCase().split('.')
  return parts.length > 1 ? parts.pop() ?? '' : ''
}

function canInlineAttachment(file: File): boolean {
  if (file.type.startsWith('text/')) return true
  return INLINE_ATTACHMENT_EXTENSIONS.has(getAttachmentExtension(file.name))
}

function canUploadAttachment(file: File): boolean {
  if (file.type.startsWith('image/')) return true
  return UPLOADABLE_ATTACHMENT_EXTENSIONS.has(getAttachmentExtension(file.name))
}

async function uploadAttachment(file: File): Promise<{ documentId?: string; error?: string }> {
  if (!canUploadAttachment(file)) {
    return {}
  }

  try {
    const { data: { session } } = await getSupabase().auth.getSession()
    if (!session?.access_token) {
      return { error: 'Manglet innlogging under opplasting.' }
    }

    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(`${API_BASE}/api/library/upload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
      body: formData,
    })

    if (!response.ok) {
      const errorText = await response.text()
      return { error: errorText || `Upload feilet med status ${response.status}` }
    }

    const data = await response.json()
    return { documentId: data.id as string | undefined }
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : 'Ukjent upload-feil',
    }
  }
}

export async function prepareMessageAttachments(attachments: PendingMessageAttachment[]): Promise<{
  metadata: MessageAttachment[]
  promptSuffix: string
}> {
  if (attachments.length === 0) {
    return { metadata: [], promptSuffix: '' }
  }

  const sections = await Promise.all(attachments.map(async attachment => {
    const uploadResult = await uploadAttachment(attachment.file)

    const metadata: MessageAttachment = {
      name: attachment.name,
      type: attachment.type || 'application/octet-stream',
      size: attachment.size,
      status: uploadResult.documentId
        ? 'uploaded'
        : canInlineAttachment(attachment.file)
          ? 'inline_only'
          : 'upload_failed',
      libraryDocumentId: uploadResult.documentId,
      note: uploadResult.error,
    }

    const lines = [
      `Filnavn: ${attachment.name}`,
      `Type: ${attachment.type || 'ukjent'}`,
      `Storrelse: ${attachment.size} bytes`,
    ]

    if (uploadResult.documentId) {
      lines.push(`Bibliotek-ID: ${uploadResult.documentId}`)
    }

    if (!canInlineAttachment(attachment.file)) {
      if (attachment.type.startsWith('image/')) {
        lines.push('Dette er et bildevedlegg. Analyser det visuelt og bruk det som kontekst i svaret.')
      } else {
        lines.push('Innhold ikke inline-lest. Behandle dette som et binart eller visuelt vedlegg.')
      }
      return { metadata, section: lines.join('\n') }
    }

    if (attachment.size > MAX_INLINE_ATTACHMENT_SIZE) {
      lines.push('Innholdet er for stort til a legges ved fullt. Oppsummer eller be om en mer spesifikk analyse ved behov.')
      return { metadata, section: lines.join('\n') }
    }

    try {
      const raw = await attachment.file.text()
      const content = raw.length > MAX_INLINE_ATTACHMENT_CHARS
        ? `${raw.slice(0, MAX_INLINE_ATTACHMENT_CHARS)}\n\n...[kuttet for lengde]`
        : raw
      lines.push('Innhold:')
      lines.push(content)
    } catch {
      lines.push('Kunne ikke lese innholdet i vedlegget automatisk.')
    }

    return { metadata, section: lines.join('\n') }
  }))

  return {
    metadata: sections.map(item => item.metadata),
    promptSuffix: [
      '',
      '',
      '[VEDLAGTE FILER]',
      'Bruk innholdet under som vedlegg i denne oppgaven.',
      ...sections.map(item => `---\n${item.section}`),
    ].join('\n'),
  }
}
